import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Building2, FileSpreadsheet, UserCircle2, LogOut,
  ChevronLeft, ChevronRight, ShieldCheck, Eye, EyeOff, Sun, Moon,
} from 'lucide-react';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useToast } from '@/contexts/ToastContext';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/super-admin', icon: <LayoutDashboard size={20} /> },
  { label: 'Organizations', path: '/super-admin/organizations', icon: <Building2 size={20} /> },
  { label: 'Audit Logs', path: '/super-admin/audit-logs', icon: <FileSpreadsheet size={20} /> },
  { label: 'Profile', path: '/super-admin/profile', icon: <UserCircle2 size={20} /> },
];

export function PlatformLayout() {
  const { user, support, logout, exitSupport } = usePlatformAuth();
  const { theme, toggleTheme } = useTheme();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const visibleNav = support ? navItems.filter(i => i.path === '/super-admin') : navItems;

  const handleExit = async () => {
    try {
      await exitSupport();
      addToast('Exited support mode', 'success');
      navigate('/super-admin');
    } catch (err: any) {
      addToast(err.message || 'Failed to exit support mode', 'error');
    }
  };

  return (
    <div className="app-shell" data-theme="swayam-preview">
      <aside className={cn('sidebar sticky top-0 z-40 h-screen flex-shrink-0 flex flex-col transition-all duration-300', collapsed ? 'w-16' : 'w-64')}>
        <div className="relative flex flex-col flex-1 min-h-0">
          <div className="flex items-center h-16 px-4 border-b flex-shrink-0" style={{ borderColor: 'var(--sidebar-border)' }}>
            {!collapsed ? (
              <div className="flex items-center gap-2">
                <ShieldCheck size={22} className="text-on-brand" />
                <span className="sidebar__brand font-heading font-semibold text-lg">LabourFlow Admin</span>
              </div>
            ) : (
              <ShieldCheck size={20} className="mx-auto text-on-brand" />
            )}
          </div>

          <button
            onClick={() => setCollapsed(!collapsed)}
            className="absolute -right-3 top-[72px] w-6 h-6 rounded-full flex items-center justify-center hover:opacity-90 transition-colors z-50 text-white"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>

          <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
            {visibleNav.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/super-admin'}
                className={({ isActive }) =>
                  cn(
                    'sidebar__nav-item flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium',
                    collapsed && 'justify-center px-2',
                    isActive && 'sidebar__nav-item--active'
                  )
                }
                title={collapsed ? item.label : undefined}
              >
                {item.icon}
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </nav>
        </div>

        {user && (
          <div className="p-3 flex-shrink-0 space-y-2" style={{ borderTop: '1px solid var(--sidebar-border)' }}>
            {support && (
              <Button variant="outline" size="sm" className="w-full justify-center gap-2" onClick={handleExit}>
                <EyeOff size={14} /> Exit Support Mode
              </Button>
            )}
            <div className="flex items-center gap-2 px-1 w-full rounded-lg py-1">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-on-brand text-sm font-medium flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary-hover)' }}>
                {user.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="sidebar__user-name text-sm font-medium truncate">{user.name}</p>
                  <p className="sidebar__user-email text-xs truncate">{user.email}</p>
                </div>
              )}
            </div>
            {!collapsed && (
              <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-error" onClick={logout}>
                <LogOut size={14} /> Sign Out
              </Button>
            )}
          </div>
        )}
      </aside>

      <div className="app-content-shell">
        <header className="app-header sticky top-0 z-30 h-16 flex items-center justify-between px-6">
          <div className="flex items-center gap-2 text-sm font-medium text-secondary">
            <ShieldCheck size={18} />
            <span className="hidden sm:block">Super Admin Console</span>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <button
              onClick={toggleTheme}
              className="theme-toggle header-theme-toggle"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </header>
        {support && (
          <div className="flex items-center justify-between gap-4 px-6 py-2 text-sm" style={{ backgroundColor: 'var(--warning)' }}>
            <div className="flex items-center gap-2 font-medium">
              <Eye size={16} />
              Support Mode: Viewing {support.organizationName || support.organizationCode}
              <span className="opacity-70 text-xs">({support.reason})</span>
            </div>
            <Button size="sm" variant="ghost" onClick={handleExit}>Exit Support Mode</Button>
          </div>
        )}
        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
